import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Select, Input, Switch, Button, notification } from "antd";
import { CopyOutlined, SaveOutlined } from "@ant-design/icons";
import "./M2MSelector.scss";
import service from "../../services/Service.js";
import { withLocale } from "../../l10n/index.jsx";
import { useGetPoolsLookupQuery } from "../../services/lookupApi.js";
import { KICONEX_UUID } from "../../utils/constants.js";

const EMPTY_ARRAY = [];

// Solo son seleccionables las SIM nuevas y sin propietario asignado
const filterAvailableSims = (list) => list.filter(item =>
  item.lifeCycleStatus === 'INACTIVE_NEW' &&
  (!item.customField1 || item.customField1.trim() === '')
);

const filterIccOptionsBySimType = (m2mArray, simType) => m2mArray
  .filter(item => simType === '4FF'
    ? item.simModel?.includes('4FF')
    : !item.simModel?.includes('4FF'))
  .map(item => ({ label: item.icc, value: item.icc }));

/**
 * Componente de seleccion de ICC y tarifa M2M. Incluye
 * alias y propietario autocalculados en base a las entidades padre.
 * 
 * @param {object} lang - Objeto de localización.
 * @param {string} icc - ICC preseleccionado para carga de datos desde Kite
 * @param {function} onIccChange - Callback cuando cambia el ICC.
 * @param {function} onKiteSaved - Callback cuando se guarda en Kite.
 * @param {string} suggestedOwner - Propietario sugerido.
 * @param {string} suggestedAlias - Alias sugerido.
 * @param {string} [defaultSimType='2FF'] - Tipo de SIM por defecto.
 * @param {boolean} disabled - Indica si el selector está deshabilitado.
 */
function M2MSelector({
  lang,
  icc,
  onIccChange,
  suggestedOwner,
  suggestedAlias,
  defaultSimType = '2FF',
  disabled,
  onKiteSaved,
}) {

  // ===== 1. ESTADOS =====
  const [ui, setUI] = useState({ 
    loadingM2M: false, 
    loadingButton: false 
  });
  const [sim, setSim] = useState({
    availableSims: [],
    simType: defaultSimType,
    selectedPool: '',
  });

  // ===== 2. SELECTORES =====
  const { data: pools = EMPTY_ARRAY } = useGetPoolsLookupQuery();

  // ===== 3. DERIVADOS =====
  const iccOptions = useMemo(
    () => filterIccOptionsBySimType(sim.availableSims, sim.simType),
    [sim.availableSims, sim.simType]
  );

  const poolOptions = useMemo(
    () => pools.map(pool => ({ label: pool.commercialGroup, value: pool.pool_id })),
    [pools]
  );

  // Sin ICC no hay nada que describir
  const owner = icc ? (suggestedOwner ?? '') : '';
  const alias = icc ? (suggestedAlias ?? '') : '';

  // ===== 4. HELPERS =====
  const updateUI = (field, value) => setUI(prev => ({ ...prev, [field]: value }));
  const updateSim = (payload) => setSim(prev => ({ ...prev, ...payload }));

  // ===== 5. FUNCIONES PRINCIPALES =====
  const loadAvailableSims = useCallback(async () => {
    updateUI('loadingM2M', true);
    try {
      const res = await service.get('m2m', { tenant_uuid: KICONEX_UUID });
      const list = (res?.data && Array.isArray(res.data)) ? filterAvailableSims(res.data) : [];
      updateSim({ availableSims: list });
    } catch (error) {
      console.error('Error al cargar opciones M2M:', error);
    } finally {
      updateUI('loadingM2M', false);
    }
  }, []);

  const preloadFromKite = useCallback(async (iccValue) => {
    updateUI('loadingM2M', true);
    try {
      const res = await service.get(`m2m/${iccValue}`);
      if (res?.data) {
        const data = res.data;
        updateSim({
          selectedPool: (data.commercialGroupId && data.commercialGroupId !== '0') ? data.commercialGroupId : '',
          simType: data.simModel?.includes('4FF') ? '4FF' : '2FF',
        });
      }
    } catch (error) {
      console.error('Error al precargar datos M2M:', error);
    } finally {
      updateUI('loadingM2M', false);
    }
  }, []);

  // ===== 6. HANDLERS =====
  const copyToClipboard = useCallback((value, fieldName) => {
    navigator.clipboard.writeText(value).then(() => {
      notification.success({
        message: `${lang.handle(fieldName)} ${lang.handle('copiado al portapapeles')}`,
      });
    }).catch(err => {
      console.error(err);
      notification.error({
        message: `${lang.handle('Error al copiar')}: ${lang.handle(fieldName)}`,
      });
    });
  }, [lang]);

  const handleSimTypeChange = useCallback((checked) => {
    updateSim({ simType: checked ? '4FF' : '2FF' });
  }, []);

  const handlePoolChange = useCallback((value) => {
    updateSim({ selectedPool: value });
  }, []);

  const handleSaveM2M = useCallback(async () => {
    if (!icc) {
      notification.warning({ message: lang.handle('ICC es obligatorio') });
      return;
    }
    if (!sim.selectedPool) {
      notification.warning({ message: lang.handle('Tarifa es obligatorio') });
      return;
    }

    updateUI('loadingButton', true);
    try {
      const res = await service.put(`m2m/${icc}`, {
        commercialGroup: sim.selectedPool,
        customField1: owner,
        alias: alias,
      });

      if (res && res.data.ok) {
        notification.success({ message: lang.handle('M2M actualizado correctamente') });
        if (onKiteSaved) onKiteSaved();
      } else {
        notification.error({ message: lang.handle('Error al actualizar M2M') });
      }
    } catch (error) {
      console.error('Error al guardar M2M:', error);
      notification.error({ message: lang.handle('Error en la operación') });
    } finally {
      updateUI('loadingButton', false);
    }
  }, [icc, sim.selectedPool, owner, alias, lang, onKiteSaved]);

  // ===== 7. EFFECTS =====
  useEffect(() => {
    loadAvailableSims();
  }, [loadAvailableSims]);

  const initialIccLoaded = useRef(false);

  useEffect(() => {
    if (initialIccLoaded.current || !icc) return;
    initialIccLoaded.current = true;
    preloadFromKite(icc);
  }, [icc, preloadFromKite]);

  useEffect(() => {
    if (icc) return;
    updateSim({ simType: defaultSimType });
  }, [defaultSimType, icc]);

  // ===== 8. RENDER =====
  return (
    <>
      {/* Fila 1: ICC + Tarifa */}
      <div className="form-group row">
        <label className="col-sm-2 col-form-label newCard-form-text"> M2M </label>
        <div className="col-sm-10 d-flex gap-2" style={{ alignItems: 'flex-start' }}>
          <div style={{ flex: 1, display: 'flex', gap: '8px', alignItems: 'center' }}>
            {!icc && (
              <Switch
                checkedChildren="4FF"
                unCheckedChildren="2FF"
                checked={sim.simType === '4FF'}
                onChange={handleSimTypeChange}
                disabled={disabled}
                className="sim-type-switch"
              />
            )}
            {icc
              ? <Input
                  value={icc}
                  onChange={(e) => onIccChange(e.target.value.replace(/\s/g, ''))}
                  disabled={disabled}
                  placeholder={lang.handle('Seleccionar ICC')}
                  style={{ flex: 1 }}
                />
              : <Select
                  className="form-control"
                  placeholder={lang.handle('Seleccionar ICC')}
                  value={undefined}
                  onChange={onIccChange}
                  disabled={disabled}
                  loading={ui.loadingM2M}
                  options={iccOptions}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  style={{ flex: 1 }}
                />
            }
            <Button onClick={() => copyToClipboard(icc, 'ICC')} disabled={!icc}>
              <CopyOutlined />
            </Button>
          </div>

          <div style={{ flex: 1 }}>
            <Select
              className="form-control"
              placeholder={lang.handle('Tarifa')}
              value={sim.selectedPool || undefined}
              onChange={handlePoolChange}
              disabled={disabled || !icc}
              options={poolOptions}
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            />
          </div>
        </div>
      </div>

      {/* Fila 2: Propietario + Alias + Boton */}
      <div className="form-group row">
        <div className="col-sm-2"></div>
        <div className="col-sm-10 d-flex gap-2" style={{ alignItems: 'flex-start' }}>
          <input
            type="text"
            className="form-control"
            placeholder={lang.handle('Propietario')}
            value={owner}
            disabled
            style={{ flex: 1 }}
          />

          <input
            type="text"
            className="form-control"
            placeholder={lang.handle('Alias')}
            value={alias}
            disabled
            style={{ flex: 1 }}
          />

          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveM2M}
            disabled={disabled || !icc || !sim.selectedPool}
            loading={ui.loadingButton}
          >
            {lang.handle('Actualizar en Kite')}
          </Button>
        </div>
      </div>
    </>
  );
}

export default withLocale(M2MSelector);
